import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Link2, Plus, Upload, Square, RotateCcw, Trash2, Cloud, Monitor, AlertTriangle, CheckCircle2, Loader2, ExternalLink
} from 'lucide-react';

/**
 * Referral Queue (admin).
 *
 * Add referral domains that point at a brand, with the brand's name, URL and
 * vertical. Each row runs the add-brand pipeline for that one domain:
 * classify (cache first) → SEMrush enrich + Hunter emails → save to
 * affiliate_clean with the brand attached → live check that the site really
 * promotes the brand. Rows are worked in batches, oldest first.
 */

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';
const BASE = `${API_URL}/api/admin/referral-queue`;

const POLL_MS = 5000;

// The server takes at most 5000 rows per request (MAX_ROWS in the route).
const MAX_PER_REQUEST = 5000;

const request = async (path, options = {}) => {
  const response = await fetch(`${BASE}${path}`, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    ...options
  });
  return response.json();
};

const VERTICALS = [
  { id: 'igaming', label: 'iGaming' },
  { id: 'trading', label: 'Trading' }
];

const MODES = [
  { id: 'local', label: 'Local (Claude)', hint: 'Desktop worker: browser → Claude WebFetch → Zyte', icon: Monitor },
  { id: 'cloud', label: 'Cloud (Claude)', hint: 'Fast — spends Zyte + Claude credits', icon: Cloud }
];

const STATUS_STYLE = {
  queued: 'bg-gray-100 text-gray-700 dark:bg-slate-800 dark:text-slate-300',
  running: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
  done: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300',
  failed: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
  stopped: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300'
};

const STAGE_LABEL = { classify: 'classifying', enrich: 'saving affiliate', live_check: 'live check' };

const VERDICT_LABEL = { affiliate: 'Affiliate', brand: 'Brand / operator', not_affiliate: 'Not affiliate' };

const LIVE_LABEL = {
  verified: { text: 'Live — promotes the brand', cls: 'text-green-600 dark:text-green-400' },
  not_live: { text: 'Brand not found on site — detached', cls: 'text-amber-600 dark:text-amber-400' },
  unreachable: { text: 'Could not check — kept attached', cls: 'text-gray-500 dark:text-slate-400' }
};

const formatWhen = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString();
};

// One per line, or separated by commas / spaces.
const splitDomains = (text) => String(text || '').split(/[\s,;]+/).map((s) => s.trim()).filter(Boolean);

// CSV columns: referral domain, then optionally brand name, brand url and
// vertical. A SEMrush referring-domains export (a "Domain" column only) works
// as is: the missing brand columns come from the form.
const HEADERS = {
  referralDomain: ['referral domain', 'referral_domain', 'referral', 'referring domain', 'domain'],
  brandName: ['brand name', 'brand_name', 'brand'],
  brandUrl: ['brand url', 'brand_url', 'brand domain', 'brand_domain'],
  vertical: ['vertical']
};

const parseCsv = (text, fallback) => {
  const lines = String(text || '').split(/\r?\n/).filter((l) => l.trim());
  if (lines.length < 2) return { error: 'The CSV has no rows.' };
  const header = lines[0];
  const sep = header.includes('\t') ? '\t' : header.includes(';') ? ';' : ',';
  const clean = (v) => String(v ?? '').trim().replace(/^"|"$/g, '');
  const names = header.split(sep).map((h) => clean(h).toLowerCase());
  const col = Object.fromEntries(Object.entries(HEADERS).map(([k, list]) => [k, names.findIndex((n) => list.includes(n))]));
  if (col.referralDomain === -1) {
    return { error: 'The CSV needs a "Referral domain" (or "Domain") column.' };
  }
  if ((col.brandName === -1 && !fallback.brandName.trim()) || (col.brandUrl === -1 && !fallback.brandUrl.trim())) {
    return { error: 'The CSV has no brand columns, so fill in the brand name and brand URL above first.' };
  }
  const pick = (v, i, alt) => (i === -1 ? '' : v[i]) || alt;
  const rows = lines.slice(1).map((line) => {
    const v = line.split(sep).map(clean);
    const vertical = pick(v, col.vertical, '').toLowerCase();
    const fallbackVertical = fallback.vertical;
    return {
      referralDomain: v[col.referralDomain],
      brandName: pick(v, col.brandName, fallback.brandName.trim()),
      brandUrl: pick(v, col.brandUrl, fallback.brandUrl.trim()),
      vertical: vertical.startsWith('trad') || vertical.startsWith('forex') ? 'trading'
        : vertical.startsWith('igam') || vertical.startsWith('gam') || vertical.startsWith('casino') ? 'igaming'
          : fallbackVertical
    };
  }).filter((r) => r.referralDomain);
  return { rows };
};

const Choice = ({ options, value, onChange, disabledIds = [] }) => (
  <div className="flex flex-wrap gap-2">
    {options.map((opt) => {
      const Icon = opt.icon;
      const disabled = disabledIds.includes(opt.id);
      const active = value === opt.id;
      return (
        <button
          key={opt.id}
          type="button"
          disabled={disabled}
          onClick={() => onChange(opt.id)}
          className={`flex items-start gap-2 rounded-lg border px-3 py-2 text-left transition-colors ${
            active
              ? 'border-blue-500 bg-blue-50 dark:border-blue-400 dark:bg-blue-900/30'
              : 'border-gray-200 bg-white hover:border-gray-300 dark:border-slate-700 dark:bg-slate-900 dark:hover:border-slate-600'
          } ${disabled ? 'cursor-not-allowed opacity-50' : ''}`}
        >
          {Icon && <Icon className="mt-0.5 h-4 w-4 text-gray-500 dark:text-slate-400" />}
          <span>
            <span className="block font-medium text-gray-900 dark:text-slate-100">{opt.label}</span>
            {opt.hint && <span className="block text-xs text-gray-500 dark:text-slate-400">{opt.hint}</span>}
          </span>
        </button>
      );
    })}
  </div>
);

const inputCls = 'w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-gray-900 focus:border-blue-500 focus:outline-none dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100';

const Outcome = ({ item }) => {
  if (!item.classification) return <span className="text-gray-400">—</span>;
  const live = LIVE_LABEL[item.live_status];
  return (
    <div className="text-xs">
      <div className={`font-medium ${item.classification === 'affiliate' ? 'text-green-600 dark:text-green-400' : 'text-gray-700 dark:text-slate-300'}`}>
        {VERDICT_LABEL[item.classification] || item.classification}
        {item.from_cache && <span className="ml-1 font-normal text-gray-400">(cached)</span>}
      </div>
      {item.affiliate_saved && <div className="text-gray-600 dark:text-slate-400">Saved to affiliate_clean</div>}
      {live && <div className={live.cls}>{live.text}</div>}
      {item.live_page_url && (
        <a href={item.live_page_url} target="_blank" rel="noreferrer"
          className="inline-flex max-w-[220px] items-center gap-1 truncate text-blue-600 hover:underline dark:text-blue-400">
          <ExternalLink className="h-3 w-3 flex-none" />
          <span className="truncate">{item.live_page_url}</span>
        </a>
      )}
    </div>
  );
};

const ReferralQueue = () => {
  const [brandName, setBrandName] = useState('');
  const [brandUrl, setBrandUrl] = useState('');
  const [vertical, setVertical] = useState('trading');
  const [mode, setMode] = useState('local');
  const [country, setCountry] = useState('us');
  const [domainsText, setDomainsText] = useState('');
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [items, setItems] = useState([]);
  const [localWorkers, setLocalWorkers] = useState(0);
  const [loading, setLoading] = useState(true);
  const fileInput = useRef(null);

  // Local (Claude) only carries the Trading prompt today.
  useEffect(() => {
    if (vertical === 'igaming' && mode === 'local') setMode('cloud');
  }, [vertical, mode]);

  const load = useCallback(async () => {
    try {
      const data = await request('');
      if (data.success) {
        setItems(data.rows || []);
        setLocalWorkers(data.localWorkers || 0);
      }
    } catch (err) {
      console.error('Failed to load referral queue:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const active = items.some((i) => i.status === 'queued' || i.status === 'running');
  useEffect(() => {
    if (!active) return undefined;
    const timer = setInterval(load, POLL_MS);
    return () => clearInterval(timer);
  }, [active, load]);

  // Sends `items` (referral domains or CSV rows) in chunks of MAX_PER_REQUEST,
  // one after another, so a file bigger than the server's per-request cap
  // still goes in with one click.
  const submit = async (key, items, extra = {}) => {
    setSaving(true);
    setMessage(null);
    let queued = 0;
    let skipped = 0;
    let own = 0;
    const plural = (n) => `${n} row${n === 1 ? '' : 's'}`;
    try {
      for (let start = 0; start < items.length; start += MAX_PER_REQUEST) {
        const chunk = items.slice(start, start + MAX_PER_REQUEST);
        const data = await request('', {
          method: 'POST',
          body: JSON.stringify({ classifyMode: mode, country, ...extra, [key]: chunk })
        });
        if (!data.success) {
          const where = items.length > MAX_PER_REQUEST ? `Rows ${start + 1}–${start + chunk.length}: ` : '';
          const done = queued ? ` (${plural(queued)} before this were queued)` : '';
          setMessage({ ok: false, text: `${where}${data.message || 'Failed to add to the queue'}${done}` });
          return false;
        }
        queued += data.queued || 0;
        skipped += data.skipped || 0;
        own += data.own || 0;
        if (start + MAX_PER_REQUEST < items.length) {
          setMessage({ ok: true, text: `Queued ${queued} of ${items.length}…` });
        }
      }
      const notes = [
        skipped && `${skipped} already in the queue`,
        own && `${own} skipped as the brand's own site`,
      ].filter(Boolean);
      setMessage({ ok: true, text: `Queued ${plural(queued)}` + (notes.length ? ` (${notes.join(', ')})` : '') });
      return true;
    } catch (err) {
      setMessage({ ok: false, text: err.message });
    } finally {
      setSaving(false);
      load();
    }
    return false;
  };

  const handleAdd = async () => {
    const referralDomains = splitDomains(domainsText);
    if (!referralDomains.length || !brandName.trim() || !brandUrl.trim()) {
      setMessage({ ok: false, text: 'Fill in the referral domain(s), brand name and brand URL.' });
      return;
    }
    if (await submit('referralDomains', referralDomains, { brandName, brandUrl, vertical })) setDomainsText('');
  };

  const handleCsv = async (file) => {
    if (!file) return;
    const parsed = parseCsv(await file.text(), { brandName, brandUrl, vertical });
    if (fileInput.current) fileInput.current.value = '';
    if (parsed.error) {
      setMessage({ ok: false, text: `${file.name}: ${parsed.error}` });
      return;
    }
    if (mode === 'local' && parsed.rows.some((r) => r.vertical === 'igaming')) {
      setMessage({ ok: false, text: `${file.name} has iGaming rows — Local (Claude) is Trading-only, so choose Cloud (Claude) for this file.` });
      return;
    }
    await submit('rows', parsed.rows);
  };

  const act = async (id, path, method = 'POST') => {
    const data = await request(`/${id}${path}`, { method });
    if (!data.success) setMessage({ ok: false, text: data.message || 'Action failed' });
    load();
  };

  return (
    <div className="p-6">
      <div className="max-w-7xl">
        <div className="mb-1 flex items-center gap-3">
          <Link2 className="h-6 w-6 text-blue-600 dark:text-blue-400" />
          <h1 className="text-xl font-semibold text-gray-900 dark:text-slate-100">Referral Queue</h1>
        </div>
        <p className="mb-6 text-gray-600 dark:text-slate-400">
          Add referral domains for a brand. Each one runs the add-brand process: classified (cache first), enriched with
          SEMrush and Hunter, saved to affiliate_clean with the brand attached, then live-checked. A site where the brand
          is not found stays in affiliate_clean but has the brand detached, as in a brand add.
        </p>

        {/* ---- Add ---- */}
        <div className="mb-6 rounded-xl border border-gray-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900">
          <div className="grid gap-4 md:grid-cols-3">
            <label className="block">
              <span className="mb-1 block font-medium text-gray-800 dark:text-slate-200">Brand name</span>
              <input className={inputCls} value={brandName} onChange={(e) => setBrandName(e.target.value)} placeholder="e.g. Pepperstone" />
            </label>
            <label className="block">
              <span className="mb-1 block font-medium text-gray-800 dark:text-slate-200">Brand URL</span>
              <input className={inputCls} value={brandUrl} onChange={(e) => setBrandUrl(e.target.value)} placeholder="e.g. pepperstone.com" />
            </label>
            <label className="block">
              <span className="mb-1 block font-medium text-gray-800 dark:text-slate-200">SEMrush database</span>
              <input className={inputCls} value={country} onChange={(e) => setCountry(e.target.value)} placeholder="us" />
            </label>
          </div>

          <div className="mt-4 grid gap-5 md:grid-cols-2">
            <div>
              <div className="mb-2 font-medium text-gray-800 dark:text-slate-200">Vertical</div>
              <Choice options={VERTICALS} value={vertical} onChange={setVertical} />
            </div>
            <div>
              <div className="mb-2 font-medium text-gray-800 dark:text-slate-200">Classify with</div>
              <Choice options={MODES} value={mode} onChange={setMode} disabledIds={vertical === 'igaming' ? ['local'] : []} />
              {vertical === 'igaming' && (
                <p className="mt-2 text-xs text-gray-500 dark:text-slate-400">
                  Local (Claude) is only available for Trading for now.
                </p>
              )}
              {mode === 'local' && (
                <p className={`mt-2 flex items-center gap-1 text-xs ${localWorkers ? 'text-green-600 dark:text-green-400' : 'text-amber-600 dark:text-amber-400'}`}>
                  {localWorkers ? <CheckCircle2 className="h-3.5 w-3.5" /> : <AlertTriangle className="h-3.5 w-3.5" />}
                  {localWorkers
                    ? `${localWorkers} local worker${localWorkers > 1 ? 's' : ''} online`
                    : 'No local worker online — start `npm run worker` in mars-local-classify, or rows fail after 30 minutes'}
                </p>
              )}
            </div>
          </div>

          <label className="mt-4 block">
            <span className="mb-1 block font-medium text-gray-800 dark:text-slate-200">Referral domain(s)</span>
            <textarea
              className={`${inputCls} min-h-[96px] font-mono text-xs`}
              value={domainsText}
              onChange={(e) => setDomainsText(e.target.value)}
              placeholder={'one per line\nexample-affiliate.com\nanother-review-site.net'}
            />
          </label>

          <div className="mt-4 flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={handleAdd}
              disabled={saving}
              className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
              Add to queue
            </button>
            <span className="text-gray-400">or</span>
            <label className="inline-flex cursor-pointer items-center gap-2 rounded-lg border border-gray-200 px-4 py-2 font-medium text-gray-700 hover:bg-gray-50 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800">
              <Upload className="h-4 w-4" />
              Upload CSV
              <input ref={fileInput} type="file" accept=".csv" className="hidden" onChange={(e) => handleCsv(e.target.files?.[0])} />
            </label>
          </div>
          <p className="mt-2 text-xs text-gray-500 dark:text-slate-400">
            CSV columns: Referral domain (or Domain), Brand name, Brand URL, Vertical. Only the domain column is required — missing brand or vertical values use the ones above, so a SEMrush referring-domains export works as is.
          </p>

          {message && (
            <p className={`mt-3 text-sm ${message.ok ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
              {message.text}
            </p>
          )}
        </div>

        {/* ---- Queue ---- */}
        <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white dark:border-slate-800 dark:bg-slate-900">
          <table className="min-w-full text-left">
            <thead className="border-b border-gray-200 bg-gray-50 text-xs uppercase text-gray-500 dark:border-slate-800 dark:bg-slate-900/60 dark:text-slate-400">
              <tr>
                <th className="px-4 py-3">#</th>
                <th className="px-4 py-3">Referral domain</th>
                <th className="px-4 py-3">Brand</th>
                <th className="px-4 py-3">Vertical</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Result</th>
                <th className="px-4 py-3">Added</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-slate-800">
              {loading && (
                <tr><td colSpan={8} className="px-4 py-6 text-center text-gray-500">Loading…</td></tr>
              )}
              {!loading && items.length === 0 && (
                <tr><td colSpan={8} className="px-4 py-6 text-center text-gray-500">Nothing queued yet.</td></tr>
              )}
              {items.map((item) => (
                <tr key={item.id} className="align-top text-gray-800 dark:text-slate-200">
                  <td className="px-4 py-3 text-gray-500">{item.id}</td>
                  <td className="px-4 py-3 font-medium">{item.referral_domain}</td>
                  <td className="px-4 py-3">
                    <div>{item.brand_name}</div>
                    <div className="text-xs text-gray-500 dark:text-slate-400">{item.brand_url}</div>
                  </td>
                  <td className="px-4 py-3">
                    <div>{item.vertical === 'trading' ? 'Trading' : 'iGaming'}</div>
                    <div className="text-xs text-gray-500 dark:text-slate-400">
                      {item.classify_mode === 'local' ? 'Local (Claude)' : 'Cloud (Claude)'} · {String(item.country || 'us').toUpperCase()}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_STYLE[item.status] || STATUS_STYLE.queued}`}>
                      {item.status === 'running' && item.stop_requested ? 'stopping'
                        : item.status === 'running' ? (STAGE_LABEL[item.stage] || 'running') : item.status}
                    </span>
                    {item.error && (
                      <div className="mt-1 max-w-[260px] text-xs text-red-600 dark:text-red-400">{item.error}</div>
                    )}
                  </td>
                  <td className="px-4 py-3"><Outcome item={item} /></td>
                  <td className="px-4 py-3 text-xs text-gray-500 dark:text-slate-400">{formatWhen(item.created_at)}</td>
                  <td className="px-4 py-3">
                    <div className="flex gap-1">
                      {(item.status === 'queued' || item.status === 'running') && !item.stop_requested && (
                        <button type="button" title="Stop" onClick={() => act(item.id, '/stop')}
                          className="rounded p-1.5 text-gray-500 hover:bg-gray-100 hover:text-red-600 dark:hover:bg-slate-800">
                          <Square className="h-4 w-4" />
                        </button>
                      )}
                      {['failed', 'stopped', 'done'].includes(item.status) && (
                        <button type="button" title="Run again" onClick={() => act(item.id, '/retry')}
                          className="rounded p-1.5 text-gray-500 hover:bg-gray-100 hover:text-blue-600 dark:hover:bg-slate-800">
                          <RotateCcw className="h-4 w-4" />
                        </button>
                      )}
                      {item.status !== 'running' && (
                        <button type="button" title="Remove" onClick={() => act(item.id, '', 'DELETE')}
                          className="rounded p-1.5 text-gray-500 hover:bg-gray-100 hover:text-red-600 dark:hover:bg-slate-800">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ReferralQueue;
